<template>
  <div v-if="user" class="flex lg:h-full">
    <LayoutSidebar @mouseleave="onSidebarLeave" @mouseenter="onSidebarEnter" />

    <!-- Mobile menu -->
    <LayoutMobileSidebar v-model="mobileMenuOpen" />

    <!-- Content area -->
    <div class="flex flex-1 flex-col overflow-hidden">
      <LayoutHeader v-model="mobileMenuOpen" />

      <!-- Main content -->
      <div class="flex flex-1 items-stretch overflow-hidden pt-16 lg:pt-0">
        <!-- Secondary nav -->
        <LayoutSecondarySidebar
          @mouseleave="onSidebarLeave"
          @mouseenter="onSidebarEnter"
        />

        <main class="flex-1 overflow-y-auto text-gray-900">
          <!-- Primary column -->
          <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last"
          >
            <!-- Content -->
            <slot />
          </section>
        </main>
      </div>
    </div>

    <LayoutTailwind />

    <Notifications />
  </div>
</template>

<script lang="ts" setup>
// @ts-expect-error no types package available
import nightwind from 'nightwind/helper';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { useUserStore } from '@/stores/user';
import { initFirebaseMessagingAndGetFCMToken } from '@/util/firebase';
import { initLanguage } from '@/util/language-utils';
import { useUiStore } from '~/stores/ui';

const route = useRoute();
const i18n = useI18n();

useHead({
  titleTemplate: (customTitle) => {
    return `${
      customTitle || i18n.t('navigation.' + route.name?.toString())
    } | Rollouter`;
  },
  htmlAttrs: {
    class: 'lg:h-full',
  },
  bodyAttrs: {
    class: 'lg:h-full bg-gray-50 lg:overflow-hidden',
  },
});

const mobileMenuOpen = ref(false);

const userStore = useUserStore();
const user = computed(() => userStore.user);

// Control UI state between mobile and desktop with absolute positioned secondary sidebar

const { onSidebarEnter, onSidebarLeave } = useUiStore();

// Global keyboard shortcuts

const isInsideOfInput = () => {
  const activeElement = document.activeElement;
  const inputs = ['input', 'select', 'textarea'];
  if (activeElement && inputs.includes(activeElement.tagName.toLowerCase())) {
    return true;
  }

  return false;
};

const handleKeyUp = (event: KeyboardEvent) => {
  if (!isInsideOfInput()) {
    if (event.key === '/') {
      document
        .getElementById('headlessui-combobox-custom-button-rollout-selection')
        ?.click();
    } else if (event.code === 'Space' && event.ctrlKey) {
      document.getElementById('global-search')?.focus();
    }
  }
};

const { $client } = useNuxtApp();

onMounted(async () => {
  window.addEventListener('keyup', handleKeyUp);
  await signInAnonymously(getAuth());
  const fcmToken = await initFirebaseMessagingAndGetFCMToken();

  if (fcmToken) {
    $client.notification.registerFCMTokenToUser
      .mutate({ fcmToken })
      .then(() => {
        console.warn('Register FCMToken successfully');
      })
      .catch((err) => {
        useErrorHandler(err);
      });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('keyup', handleKeyUp);
});

// Language

initLanguage(userStore.user?.language, i18n);

// Dark mode

nightwind.enable(userStore.user?.settings?.enableDarkMode);
</script>
